import React from "react";

export default function LayersList({ layers, selectedId, setSelectedId, bringToFront, deleteLayer, lockedLayers = [], toggleLockLayer, canvas }) {
  const getLayerIcon = (type) => {
    switch (type) {
      case "text": return "📝";
      case "image": return "🖼️";
      case "video": return "🎬";
      case "audio": return "🎵";
      case "gif": return "🎭";
      default: return "📄";
    }
  };

  const getLayerPreview = (layer) => {
    if (layer.type === "image" || layer.type === "gif") {
      return (
        <img 
          src={layer.content} 
          alt="preview"
          style={{
            width: 40,
            height: 40,
            objectFit: "cover",
            borderRadius: 6,
            border: "1px solid var(--border-color)"
          }}
        />
      );
    }
    if (layer.type === "video") {
      return (
        <div style={{
          width: 40,
          height: 40,
          background: "#202024",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 16
        }}>
          🎥
        </div>
      );
    }
    if (layer.type === "text") {
      return (
        <div style={{
          width: 40,
          height: 40,
          background: "var(--success-color)",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 12,
          textAlign: "center",
          padding: 4
        }}>
          {layer.content?.substring(0, 3) || "نص"}
        </div>
      );
    }
    return (
      <div style={{
        width: 40,
        height: 40,
        background: "var(--bg-secondary)",
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-secondary)"
      }}>
        {getLayerIcon(layer.type)}
      </div>
    );
  };

  const getLayerTitle = (layer) => {
    if (layer.type === "text") {
      return layer.content?.substring(0, 20) || "نص جديد";
    }
    if (layer.type === "image" || layer.type === "gif") {
      const fileName = layer.content?.split('/').pop() || "صورة";
      return fileName.length > 15 ? fileName.substring(0, 15) + "..." : fileName;
    }
    if (layer.type === "video") {
      const fileName = layer.content?.split('/').pop() || "فيديو";
      return fileName.length > 15 ? fileName.substring(0, 15) + "..." : fileName;
    }
    if (layer.type === "audio") {
      const fileName = layer.content?.split('/').pop() || "صوت";
      return fileName.length > 15 ? fileName.substring(0, 15) + "..." : fileName;
    }
    return "طبقة جديدة";
  };

  // الحصول على المجموعات التي تنتمي إليها الطبقة
  const getLayerGroups = (layer) => {
    if (!canvas?.groups) return [];
    return canvas.groups.filter(group => group.layers && group.layers.includes(layer.id));
  };

  return (
    <div style={{ maxHeight: 300, overflowY: "auto" }}>
      {layers.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "var(--text-secondary)",
          fontSize: 14
        }}>
          📋 لا توجد طبقات بعد
          <br />
          <span style={{ fontSize: 12 }}>اضغط على الأزرار أعلاه لإضافة طبقات</span>
        </div>
      ) : (
        layers.map((layer) => {
          const isLocked = lockedLayers.includes(layer.id);
          return (
            <div key={layer.id}
              style={{
                background: layer.id === selectedId ? "var(--bg-secondary)" : isLocked ? "#e0e7ef" : "var(--bg-card)",
                margin: "8px 0",
                borderRadius: 8,
                padding: 12,
                cursor: "pointer",
                border: layer.id === selectedId ? "2px solid var(--button-bg)" : isLocked ? "2px solid #0ea5e9" : "1px solid var(--border-color)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 12,
                opacity: layer.visible === false ? 0.6 : 1
              }}
              onClick={() => setSelectedId(layer.id)}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px var(--shadow-color)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              {/* الصورة المصغرة */}
              {getLayerPreview(layer)}
              
              {/* معلومات الطبقة */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: layer.id === selectedId ? "bold" : "normal",
                  color: "var(--text-primary)",
                  fontSize: 14,
                  marginBottom: 4
                }}>
                  {getLayerTitle(layer)}
                  {isLocked && <span style={{marginRight: 6, color: '#0ea5e9', fontSize: 16}} title="طبقة مقفولة">🔒</span>}
                </div>
                <div style={{
                  fontSize: 11,
                  color: "var(--text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap"
                }}>
                  <span>{getLayerIcon(layer.type)} {layer.type === "text" ? "نص" : 
                    layer.type === "image" ? "صورة" :
                    layer.type === "video" ? "فيديو" :
                    layer.type === "audio" ? "صوت" :
                    layer.type === "gif" ? "GIF" : "طبقة"}</span>
                  <span>•</span>
                  <span>{Math.round(layer.opacity * 100)}%</span>
                  {layer.visible === false && (
                    <>
                      <span>•</span>
                      <span style={{ color: "var(--danger-color)" }}>مخفية</span>
                    </>
                  )}
                  {getLayerGroups(layer).length > 0 && (
                    <>
                      <span>•</span>
                      <span style={{ color: "var(--success-color)" }}>
                        📁 {getLayerGroups(layer).map(g => g.name).join(', ')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              {/* أزرار التحكم */}
              <div style={{ display: "flex", gap: 4 }}>
                {/* زر القفل */}
                <button
                  onClick={e => { e.stopPropagation(); toggleLockLayer(layer.id); }}
                  style={{
                    background: isLocked ? '#0ea5e9' : 'var(--bg-card)',
                    color: isLocked ? '#fff' : '#0ea5e9',
                    border: `1px solid #0ea5e9`,
                    borderRadius: 4,
                    padding: '4px 8px',
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: isLocked ? 'bold' : 'normal'
                  }}
                  title={isLocked ? 'إلغاء القفل' : 'قفل الطبقة'}
                >
                  {isLocked ? '🔒' : '🔓'}
                </button>
                <button 
                  onClick={e => { e.stopPropagation(); bringToFront(layer.id); }}
                  style={{
                    background: "var(--bg-card)",
                    color: "var(--button-bg)",
                    border: "1px solid var(--button-bg)",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "var(--button-bg)";
                    e.target.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "var(--bg-card)";
                    e.target.style.color = "var(--button-bg)";
                  }}
                  title="إحضار للأمام"
                >
                  🔝
                </button>
                <button 
                  onClick={e => { e.stopPropagation(); deleteLayer(layer.id); }}
                  style={{
                    background: "#fff",
                    color: "#f44336",
                    border: "1px solid #f44336",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f44336";
                    e.target.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                    e.target.style.color = "#f44336";
                  }}
                  title="حذف الطبقة"
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
